import { Check, Copy, Eye, FilePlus2, MoreHorizontal, Pencil, Printer, Send, X } from "lucide-react";
import { Link } from "@/lib/nav";
import { useRouter } from "@/lib/nav";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@buildpy/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@buildpy/ui/dropdown-menu";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import { formatDate, formatDateTime } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import {
  createNewVersion,
  decideQuote,
  sendQuote,
} from "@/api/actions/quote";
import {
  canCreateNewVersion,
  canDecide,
  canEditDraft,
  canSend,
} from "@buildpy/core/modules/quote/transitions";
import type { VatMode } from "@buildpy/core/modules/money/vat";
import { announceSaved } from "@buildpy/core/modules/quote/editor/saved-elsewhere";
import type { PriceCandidate } from "@buildpy/core/modules/quote/price-candidates";
import { QuoteStatus } from "@buildpy/core/modules/quote/types";
import SentLinkNote from "@/modules/share/ui/sent-link-note";
import PriceCandidatesSection from "@/modules/quote/ui/detail/price-candidates-section";

interface Props {
  quoteId: string;
  status: QuoteStatus;
  /** 발송된 견적의 공유 토큰. 없으면 링크 관련 버튼을 숨긴다. */
  token: string | null;
  shareExpiresAt: string | null;
  viewedAt: string | null;
  viewCount: number;
  /** 발송 다이얼로그 기본값 — 고객 휴대폰 끝 4자리. */
  defaultLast4: string;
  /** 발송 다이얼로그 기본 만료일(yyyy-mm-dd). */
  defaultExpiresDate: string;
  /**
   * 이 화면이 **계열의 최신 버전**인가.
   *
   * 옛 버전에서도 「새 버전」 을 누를 수 있으면 v3 가 최신본이 아니라 옛 내용을
   * 복사해 오고, 살아 있는 최신 링크까지 회수된다. 저장소가 막고 있지만 버튼이
   * 보이면 누르고 나서야 오류를 본다 — 보이지 않는 편이 맞다.
   */
  isLatest: boolean;
  // 새 품목 후보의 판매가를 이 견적 방식으로 보여 준다.
  vatMode: VatMode;
}

/** 날짜 입력은 하루의 끝까지 살려 준다 — 만료일 당일 오전에 링크가 죽으면 사고다. */
const endOfDayIso = (date: string): string => {
  const parsed = new Date(`${date}T23:59:59`);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString();
};

const shareUrlOf = (token: string): string =>
  typeof window === "undefined"
    ? `/s/${token}`
    : `${window.location.origin}/s/${token}`;

/**
 * 상세 화면의 액션 바. 어떤 버튼이 보이는지는 전이표(transitions)가 정한다 —
 * 여기서 상태를 다시 판단하면 저장소와 화면이 갈라진다.
 */
const QuoteActions = ({
  quoteId,
  status,
  token,
  shareExpiresAt,
  viewedAt,
  viewCount,
  defaultLast4,
  defaultExpiresDate,
  isLatest,
  vatMode,
}: Props) => {
  const router = useRouter();
  const [pending, setPending] = useState(false);

  const [sendOpen, setSendOpen] = useState(false);
  const [last4, setLast4] = useState(defaultLast4);
  const [expiresDate, setExpiresDate] = useState(defaultExpiresDate);
  const [sentUrl, setSentUrl] = useState<string | null>(null);
  // 발송 결과의 새 품목 후보 — 비면 저장 묻기를 안 띄운다.
  const [candidates, setCandidates] = useState<PriceCandidate[]>([]);
  /** 「남이 편집 중」 경고. 뜬 상태에서 다시 누르면 그대로 보낸다. */
  const [sendWarning, setSendWarning] = useState<string | null>(null);

  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [viewOpen, setViewOpen] = useState(false);

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("링크를 복사했습니다");
    } catch {
      toast.error("복사하지 못했습니다 — 링크를 직접 선택해 복사해 주세요");
    }
  }, []);

  /**
   * 발송. **남이 편집 중이면 한 번 더 묻는다.**
   *
   * 발송은 그 견적의 초안을 전부 치우는데, 발송본은 편집기가 안 열려 그 편집을
   * 되살릴 길이 없다. 서버가 막아 주면 그 문구를 그대로 보여 주고, 같은 버튼을
   * 다시 누를 때만 `force` 로 보낸다(저장의 「그래도 덮어쓰기」 와 같은 방식).
   *
   * 「확인하면 뚫리는 실패」 인지는 **문구가 아니라 `needs_confirm`** 으로 가른다 —
   * 문구를 고치는 순간 2단 확인이 조용히 사라지면 안 된다.
   */
  const send = useCallback(
    async (force = false) => {
      setPending(true);
      try {
        const result = await sendQuote(
          quoteId,
          { last4, expires_at: endOfDayIso(expiresDate) },
          force
        );
        if (!result.ok) {
          if (!force && result.needs_confirm) {
            setSendWarning(result.message);
            return;
          }
          // 「그래도 발송」 이 **다른 이유**(만료일 등)로 실패했으면 경고를 내린다.
          // 안 내리면 버튼이 계속 「그래도 발송」 이라, 그 이유를 고치고 누를 때
          // 재확인 없이 그대로 나간다.
          setSendWarning(null);
          toast.error(result.message);
          return;
        }
        // 발송도 rev 를 올린다 — 같은 견적을 연 다른 탭 편집기를 잠근다(편집 내용 복사는 남는다).
        setSendWarning(null);
        announceSaved({ quote_id: quoteId, rev: result.rev, kind: "sent", head: result.lock_head });
        setSentUrl(result.share_url);
        setCandidates(result.newPriceCandidates);
        toast.success("발송했습니다 — 링크를 고객에게 보내 주세요");
        router.refresh();
      } catch (error) {
        // 이동(세션 만료 401 → 세션 정리·/login 포함)은 settleRedirect 가 처리한다 — 토스트로 삼키지 않는다.
        if (settleRedirect(error)) return;
        toast.error("발송하지 못했습니다");
      } finally {
        setPending(false);
      }
    },
    [quoteId, last4, expiresDate, router]
  );

  // 발송 결과 닫기 — 닫기 버튼과 새 품목 저장·저장 안 함이 같은 길로 끝낸다.
  const closeSent = useCallback(() => {
    setSentUrl(null);
    setCandidates([]);
    setSendOpen(false);
  }, []);

  const decide = useCallback(
    async (decision: "WON" | "LOST", reason?: string) => {
      setPending(true);
      try {
        const result = await decideQuote(quoteId, decision, reason ?? null);
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        // 확정도 rev 를 올린다 — 같은 견적을 연 편집기 탭의 잠김 문구를 수주·이탈 마감으로 바꾼다.
        announceSaved({ quote_id: quoteId, rev: result.rev, kind: decision === "WON" ? "won" : "lost" });
        toast.success(decision === "WON" ? "수주로 확정했습니다" : "이탈로 확정했습니다");
        setLostOpen(false);
        // 고객 단계는 서버가 사건(수주·이탈)으로 따라 옮긴다 — 여기서 묻지 않는다.
        router.refresh();
      } catch (error) {
        // 이동(세션 만료 401 → 세션 정리·/login 포함)은 settleRedirect 가 처리한다 — 토스트로 삼키지 않는다.
        if (settleRedirect(error)) return;
        toast.error("변경하지 못했습니다");
      } finally {
        setPending(false);
      }
    },
    [quoteId, router]
  );

  const newVersion = useCallback(async () => {
    setPending(true);
    try {
      const result = await createNewVersion(quoteId);
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      // 원본 번호는 안 오르지만 원본을 연 편집기 탭은 「최신 버전에서 이어가세요」 로 잠근다.
      announceSaved({ quote_id: quoteId, kind: "not_latest" });
      toast.success(`v${result.version} 초안을 만들었습니다`);
      router.push(`/app/quotes/${result.quote_id}/edit`);
    } catch (error) {
      // 이동(세션 만료 401 → 세션 정리·/login 포함)은 settleRedirect 가 처리한다 — 토스트로 삼키지 않는다.
      if (settleRedirect(error)) return;
      toast.error("새 버전을 만들지 못했습니다");
    } finally {
      setPending(false);
    }
  }, [quoteId, router]);

  const canDecideHere = canDecide(status) && isLatest;
  const canBranch = canCreateNewVersion(status) && isLatest;
  const hasLink = token !== null && canCreateNewVersion(status);
  // 화면에 주 버튼은 하나 — 초안은 「발송」, 발송한 최신본은 「링크 복사」, 나머지는 없다.
  const linkIsPrimary = hasLink && isLatest && !canSend(status);
  const hasMore = canDecideHere || hasLink || !canEditDraft(status);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canEditDraft(status) && (
        <Button asChild size="sm" variant="outline">
          <Link href={`/app/quotes/${quoteId}/edit`}>
            <Pencil />
            편집
          </Link>
        </Button>
      )}

      {canBranch && (
        <Button
          disabled={pending}
          size="sm"
          variant="outline"
          onClick={() => void newVersion()}>
          <FilePlus2 />새 버전
        </Button>
      )}

      {canSend(status) && (
        <Button
          disabled={pending}
          size="sm"
          variant="primary"
          onClick={() => {
            setSentUrl(null);
            setSendOpen(true);
          }}>
          <Send />
          발송
        </Button>
      )}

      {hasLink && token && (
        <Button
          disabled={pending}
          size="sm"
          variant={linkIsPrimary ? "primary" : "outline"}
          onClick={() => void copy(shareUrlOf(token))}>
          <Copy />
          링크 복사
        </Button>
      )}

      {hasMore && (
        // 메뉴에서 대화상자를 여므로 비모달 — 모달이면 닫히며 body 의 pointer-events 가 남는다.
        <DropdownMenu modal={false}>
          {/* 비활성은 Trigger 에 단다 — 자식 Button 에만 달면 Radix 가 포인터다운으로 빈 메뉴를 연다. */}
          <DropdownMenuTrigger asChild disabled={pending}>
            <Button aria-label="더보기" size="sm" variant="ghost">
              <MoreHorizontal />
              더보기
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            {canDecideHere && (
              <DropdownMenuItem onSelect={() => void decide("WON")}>
                <Check />
                수주로 확정
              </DropdownMenuItem>
            )}
            {hasLink && (
              <DropdownMenuItem onSelect={() => setViewOpen(true)}>
                <Eye />
                열람 기록
              </DropdownMenuItem>
            )}
            {!canEditDraft(status) && (
              <DropdownMenuItem asChild>
                <Link href={`/app/quotes/${quoteId}/print`}>
                  <Printer />
                  인쇄
                </Link>
              </DropdownMenuItem>
            )}
            {/* 이탈은 수주와 떨어뜨려 맨 아래 — 잘못 누르면 되돌리기 어렵다. */}
            {canDecideHere && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => setLostOpen(true)}>
                  <X />
                  이탈 처리
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* ── 발송 ─────────────────────────────────────────────── */}
      <Dialog
        open={sendOpen}
        onOpenChange={(open) => {
          if (!open) {
            // 닫았다 다시 열면 경고는 지운다 — 옛 경고를 보고 「그래도 발송」 을
            // 누르면 묻지도 않고 나간다.
            setSendWarning(null);
            // 발송 결과도 같이 치운다. 안 치우면 다시 열었을 때 **지난 링크가
            // 담긴 「발송했습니다」** 가 그대로 떠, 이미 보낸 줄 알고 넘어간다.
            setSentUrl(null);
            setCandidates([]);
          }
          setSendOpen(open);
        }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{sentUrl ? "발송했습니다" : "견적 발송"}</DialogTitle>
            {/* 안내 문단이 곧 설명이다 — 따로 쓰면 같은 말이 화면에 두 번 간다. */}
            <DialogDescription>
              {sentUrl ? (
                <SentLinkNote last4={last4} docName="견적서" />
              ) : (
                "고객이 열어 볼 링크를 만듭니다. 뒤 4자리와 만료일이 열쇠입니다."
              )}
            </DialogDescription>
          </DialogHeader>

          {sentUrl ? (
            <div className="flex flex-col gap-y-3">
              <div className="flex items-center gap-x-2">
                <Input readOnly aria-label="고객 링크" inputSize="sm" name="sent-url" value={sentUrl} />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void copy(sentUrl)}>
                  <Copy />
                  복사
                </Button>
              </div>
              {candidates.length > 0 ? (
                <PriceCandidatesSection key={sentUrl} candidates={candidates} quoteId={quoteId} vatMode={vatMode} onDone={closeSent} />
              ) : (
                <DialogFooter>
                  <Button size="sm" variant="primary" onClick={closeSent}>
                    닫기
                  </Button>
                </DialogFooter>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-y-3">
              <Field label="고객 휴대폰 뒤 4자리">
                <Input
                  inputMode="numeric"
                  inputSize="sm"
                  maxLength={4}
                  name="last4"
                  value={last4}
                  onChange={(e) =>
                    setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                />
              </Field>

              <Field label="링크 만료일">
                <Input
                  inputSize="sm"
                  name="expires-date"
                  type="date"
                  value={expiresDate}
                  onChange={(e) => setExpiresDate(e.target.value)}
                />
              </Field>

              {sendWarning && (
                <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
                  {sendWarning}
                </p>
              )}

              <DialogFooter>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSendWarning(null);
                    setSendOpen(false);
                  }}>
                  취소
                </Button>
                <Button
                  disabled={pending || last4.length !== 4 || !expiresDate}
                  size="sm"
                  variant={sendWarning ? "destructive" : "primary"}
                  onClick={() => void send(sendWarning !== null)}>
                  {sendWarning ? "그래도 발송" : "발송"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── 이탈 사유 ────────────────────────────────────────── */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>이탈 처리</DialogTitle>
            <DialogDescription>
              놓친 이유를 남깁니다. 나중에 이탈 사유를 모아 봅니다.
            </DialogDescription>
          </DialogHeader>

          <Field label="사유 (필수 · 300자)">
            {/* 사유는 필수다 — 고객 단계도 이탈로 옮길 때 같은 사유를 그대로 쓴다. */}
            <Textarea
              className="min-h-24 text-sm"
              maxLength={300}
              placeholder="예) 예산 차이 · 타사 선택"
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
            />
          </Field>

          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setLostOpen(false)}>
              취소
            </Button>
            <Button
              disabled={pending || lostReason.trim().length === 0}
              size="sm"
              variant="destructive"
              onClick={() => void decide("LOST", lostReason)}>
              이탈로 확정
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 열람 기록 ────────────────────────────────────────── */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>열람 기록</DialogTitle>
            <DialogDescription>
              고객이 링크를 언제, 몇 번 열었는지입니다.
            </DialogDescription>
          </DialogHeader>

          <dl className="flex flex-col gap-y-2 text-sm">
            <div className="flex justify-between gap-x-4">
              <dt className="text-muted-foreground">최초 열람</dt>
              <dd className="tabular-nums">
                {viewedAt ? formatDateTime(viewedAt) : "아직 열어 보지 않음"}
              </dd>
            </div>
            <div className="flex justify-between gap-x-4">
              <dt className="text-muted-foreground">열람 횟수</dt>
              <dd className="tabular-nums">{viewCount}회</dd>
            </div>
            <div className="flex justify-between gap-x-4">
              <dt className="text-muted-foreground">링크 만료</dt>
              <dd className="tabular-nums">{formatDate(shareExpiresAt)}</dd>
            </div>
          </dl>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteActions;
